/**
 * RunOverviewSection — the run-record page's top section (26742): who/what/
 * when. Composed entirely from existing shared pieces so the dedicated URL
 * introduces no new visual vocabulary:
 *   - `PinnedHeader` for client/policy identity,
 *   - `WorkflowStatusBadge` for the status pill (with the same `*` warnings
 *     marker the Monitor grid uses),
 *   - `RunSection` for the Triggered by / Started / Ended / Duration /
 *     Current step / Progress / Version field set.
 *
 * Presentation only — the route loader computes every formatted value.
 */
import type { ReactNode } from "react";
import PinnedHeader from "../drawer/PinnedHeader";
import { RunSection } from "../drawer/sections/RunSection";
import WorkflowStatusBadge from "~/features/workflows/components/WorkflowStatusBadge";
import type { WaitSignals } from "~/common/workflows/statusPill";
import type { WorkflowStatus } from "~/features/workflows/monitor.types";

export interface RunOverviewSectionProps {
  clientName: string | null;
  policyNumber: string | null;
  workflowTitle: string | null;
  status: WorkflowStatus;
  hasWarnings?: boolean;
  needsAcknowledgement?: boolean | null;
  /** Who a parked run waits on, for the pill (`WorkflowStatusBadge`). */
  waitSignals?: WaitSignals;
  triggeredByName: string | null;
  startedAtFormatted: string | null;
  endedAtFormatted: string | null;
  duration: string | null;
  currentStep: string | null;
  progress: string | null;
  workflowVersion: string | null;
  /** Rendered at the RIGHT of the status row. The record puts its
   *  Servicer/All-details toggle here rather than on a row of its own —
   *  the status badge leaves that half of the line empty, and a dedicated
   *  row for one control was the loosest thing on the page. */
  statusRowAdornment?: ReactNode;
}

export function RunOverviewSection({
  clientName,
  policyNumber,
  workflowTitle,
  status,
  hasWarnings = false,
  needsAcknowledgement = null,
  waitSignals,
  triggeredByName,
  startedAtFormatted,
  endedAtFormatted,
  duration,
  currentStep,
  progress,
  workflowVersion,
  statusRowAdornment,
}: RunOverviewSectionProps) {
  return (
    <div className="flex flex-col gap-3" data-testid="run-overview-section">
      <PinnedHeader
        clientName={clientName}
        policyNumber={policyNumber}
        rightLabel={workflowTitle}
        rightLabelTitle="Workflow"
      />
      <div
        className="flex items-center justify-between gap-2 px-1"
        data-testid="run-overview-status"
      >
        <WorkflowStatusBadge status={status} hasWarnings={hasWarnings} needsAcknowledgement={needsAcknowledgement} waitSignals={waitSignals} />
        {statusRowAdornment ?? null}
      </div>
      <RunSection
        triggeredByName={triggeredByName}
        startedAtFormatted={startedAtFormatted}
        endedAtFormatted={endedAtFormatted}
        duration={duration}
        currentStep={currentStep}
        progress={progress}
        workflowVersion={workflowVersion}
        defaultExpanded
      />
    </div>
  );
}

export default RunOverviewSection;
